import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Flex, Heading, HStack, VStack, Text, IconButton, Button,
    Grid, GridItem, Badge, Spinner, useDisclosure, useBreakpointValue,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalCloseButton, ModalFooter, Divider, Checkbox,
    Select, useToast
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiUser, FiX, FiSearch } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';
import { useAuth } from '../context/AuthContext';
import LunchSearchModal from '../components/LunchSearchModal';
import LunchBatchModal from '../components/LunchBatchModal';
import MobileMonthCalendar from '../mobile/components/MobileMonthCalendar';

// ── 工具函式 ──────────────────────────────────────────────
const getTaipeiToday = () =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());

const toDateStr = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

function buildCalendarCells(year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const offset = firstDay.getDay(); // 週日 = 0（JS 原生）
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const dow = new Date(year, month - 1, d).getDay(); // 0=Sun, 6=Sat
        cells.push({ day: d, dateStr: toDateStr(year, month, d), isWeekend: dow === 0 || dow === 6 });
    }
    return cells;
}

// ── 主元件 ──────────────────────────────────────────────
const LunchManagementPage = () => {
    const { user } = useAuth();
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const today = getTaipeiToday();
    const [now] = useState(() => new Date());
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);

    // 月曆資料
    const [monthlyCounts, setMonthlyCounts] = useState({});
    const [loadingMonth, setLoadingMonth] = useState(false);

    // 跨日選取
    const [selectedDates, setSelectedDates] = useState(new Set());

    // 單日彈窗
    const { isOpen: isDayOpen, onOpen: openDay, onClose: closeDay } = useDisclosure();
    const [dayTarget, setDayTarget] = useState(null);
    const [dayClassId, setDayClassId] = useState('');
    const [loadingDay, setLoadingDay] = useState(false);
    const [dayOrders, setDayOrders] = useState([]);
    const [dayStudents, setDayStudents] = useState([]);
    const [selectedToAdd, setSelectedToAdd] = useState(new Set());
    const [submittingDay, setSubmittingDay] = useState(false);

    // 跨日彈窗
    const { isOpen: isBatchOpen, onOpen: openBatch, onClose: closeBatch } = useDisclosure();

    // 搜尋學生彈窗
    const { isOpen: isSearchOpen, onOpen: openSearch, onClose: closeSearch } = useDisclosure();

    // 班級列表
    const [classes, setClasses] = useState([]);

    const cells = buildCalendarCells(viewYear, viewMonth);

    // 掛載時 fetchClasses() 自己的鏈已經會用「剛載入、正確」的班級去呼叫一次 fetchMonthlySummary；
    // 這支 effect 若在掛載當下也跟著觸發，會用尚未載入(空陣列)的 classes 再呼叫一次，
    // 兩個請求競爭、晚到的蓋掉先到的 → 月曆人數「閃一下消失」。跳過第一次，只在使用者切月份時才觸發。
    const isFirstMonthEffect = useRef(true);
    useEffect(() => { if (user) fetchClasses(); }, [user]);
    useEffect(() => {
        if (isFirstMonthEffect.current) { isFirstMonthEffect.current = false; return; }
        fetchMonthlySummary();
    }, [viewYear, viewMonth]);

    const prevMonth = () => {
        if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
        else setViewMonth(m => m - 1);
        setSelectedDates(new Set());
    };
    const nextMonth = () => {
        if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
        else setViewMonth(m => m + 1);
        setSelectedDates(new Set());
    };

    const fetchClasses = async () => {
        try {
            const res = await authAPI.getClassesLite();
            const filtered = filterClassesForStaffUser(res.data, user);
            setClasses(filtered);
            await fetchMonthlySummary(filtered);  // 傳入剛取得的 filtered 確保正確
        } catch (e) { console.error(e); }
    };

    const fetchMonthlySummary = async (overrideClasses) => {
        setLoadingMonth(true);
        try {
            const effectiveClasses = overrideClasses ?? classes;
            // Teacher 只看自己班；Admin/Receptionist 看全機構
            const isAdminRole = ['admin', 'super_admin', 'receptionist'].includes(user?.role);
            const summaryClassId = isAdminRole ? undefined : (effectiveClasses.length > 0 ? effectiveClasses[0].id : undefined);
            const res = await authAPI.lunch.getMonthlySummary(viewYear, viewMonth, summaryClassId);
            const map = {};
            res.data.forEach(({ date, count }) => { map[date] = count; });
            setMonthlyCounts(map);
        } catch (e) { console.error(e); }
        finally { setLoadingMonth(false); }
    };

    const fetchDayOrders = useCallback(async (dateStr, classId) => {
        setLoadingDay(true);
        try {
            const [ordersRes, studentsRes] = await Promise.all([
                authAPI.lunch.getOrders({
                    start_date: dateStr,
                    end_date: dateStr,
                    ...(classId ? { class_id: classId } : {})  // 只在有 classId 時才傳
                }),
                classId
                    ? authAPI.getStudents({ class_id: classId, limit: 200 })
                    : Promise.resolve({ data: [] })  // 全部班級時不顯示未訂餐學生
            ]);
            setDayOrders(ordersRes.data);
            setDayStudents(studentsRes.data);
            setSelectedToAdd(new Set());
        } catch (e) { console.error(e); }
        finally { setLoadingDay(false); }
    }, []);

    // 圓圈點擊 → 跨日選取
    const toggleDateSelection = (e, dateStr) => {
        e.stopPropagation();
        setSelectedDates(prev => {
            const next = new Set(prev);
            next.has(dateStr) ? next.delete(dateStr) : next.add(dateStr);
            return next;
        });
    };

    // 格子點擊 → 單日彈窗
    const handleCellClick = (dateStr) => {
        setDayTarget(dateStr);
        fetchDayOrders(dateStr, dayClassId);
        openDay();
    };

    // 單日：新增訂餐
    const handleAddOrders = async () => {
        if (selectedToAdd.size === 0) return;
        setSubmittingDay(true);
        try {
            await Promise.all(
                Array.from(selectedToAdd).map(studentId =>
                    authAPI.lunch.batchOrder({ student_id: studentId, dates: [dayTarget] })
                )
            );
            toast({ title: `新增 ${selectedToAdd.size} 筆訂餐`, status: 'success' });
            fetchDayOrders(dayTarget, dayClassId);
            fetchMonthlySummary();
        } catch (e) {
            toast({ title: '新增失敗', status: 'error' });
        } finally { setSubmittingDay(false); }
    };

    // 單日：取消訂餐
    const handleCancelOrder = async (orderId) => {
        try {
            await authAPI.lunch.cancelOrder(orderId);
            toast({ title: '已取消', status: 'success' });
            fetchDayOrders(dayTarget, dayClassId);
            fetchMonthlySummary();
        } catch (e) { toast({ title: '取消失敗', status: 'error' }); }
    };

    // 單日彈窗中：已訂 / 未訂
    const orderedIds = new Set(dayOrders.map(o => o.student_id));
    const notOrdered = dayStudents.filter(s => !orderedIds.has(s.id));

    const numRows = Math.ceil(cells.length / 7);

    return (
        <Box h="100%" display="flex" flexDirection="column" px={{ base: 3, md: 4 }} pt={{ base: 3, md: 6 }} pb={3}>
            {/* 標題 + 月份導覽 */}
            <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" mb={4} gap={2}>
                <HStack spacing={{ base: 1.5, md: 3 }}>
                    <Heading size="lg" color="brand.800" display={{ base: 'none', md: 'block' }}>訂餐管理</Heading>
                    <Button leftIcon={<FiSearch />} variant="outline" size={{ base: 'xs', md: 'sm' }} onClick={openSearch}>
                        <Box as="span" display={{ base: 'none', md: 'inline' }}>搜尋學生</Box>
                    </Button>
                </HStack>
                {!isMobile && (
                    <HStack
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        boxShadow="xs"
                        px={3} py={1} justify="center"
                    >
                        <IconButton icon={<FiChevronLeft />} onClick={prevMonth} size="sm" variant="ghost" aria-label="上個月" />
                        <Text fontWeight="bold" minW="110px" textAlign="center">
                            {viewYear}年 {viewMonth}月
                            {loadingMonth && <Spinner size="xs" ml={2} color="orange.400" />}
                        </Text>
                        <IconButton icon={<FiChevronRight />} onClick={nextMonth} size="sm" variant="ghost" aria-label="下個月" />
                    </HStack>
                )}
            </Flex>

            {isMobile ? (
                <Box flex="1" minH={0} overflowY="auto">
                    <MobileMonthCalendar
                        year={viewYear} month={viewMonth} today={today} loading={loadingMonth}
                        onPrevMonth={prevMonth} onNextMonth={nextMonth}
                        onDayClick={handleCellClick}
                        accentColor="orange" rowHeight="63px"
                        getCellStyle={(dateStr) => {
                            const count = monthlyCounts[dateStr] || 0;
                            if (count > 0) return { bg: 'orange.50' };
                            return undefined;
                        }}
                        renderDay={(dateStr) => {
                            const count = monthlyCounts[dateStr] || 0;
                            const isSelected = selectedDates.has(dateStr);
                            return (
                                <>
                                    <Box
                                        pos="absolute" top="3px" right="3px"
                                        w="14px" h="14px" borderRadius="full"
                                        border="2px solid"
                                        borderColor={isSelected ? 'orange.500' : 'gray.300'}
                                        bg={isSelected ? 'orange.500' : 'transparent'}
                                        cursor="pointer"
                                        onClick={(e) => toggleDateSelection(e, dateStr)}
                                        zIndex={1}
                                    />
                                    {count > 0 && (
                                        <Text pos="absolute" bottom="4px" left="0" right="0" textAlign="center"
                                            fontSize="sm" fontWeight="bold" color="orange.600">
                                            {count}
                                        </Text>
                                    )}
                                </>
                            );
                        }}
                    />
                    {/* 使用說明 */}
                    <Box mt={3} px={1}>
                        <HStack spacing={4} flexWrap="wrap" gap={2}>
                            <HStack spacing={1.5}>
                                <Box w="12px" h="12px" borderRadius="sm" bg="orange.50" border="1px solid" borderColor="orange.200" />
                                <Text fontSize="xs" color="var(--fg-muted)">有訂餐</Text>
                            </HStack>
                            <HStack spacing={1.5}>
                                <Box w="12px" h="12px" borderRadius="full" border="2px solid" borderColor="orange.500" bg="orange.500" />
                                <Text fontSize="xs" color="var(--fg-muted)">已勾選</Text>
                            </HStack>
                        </HStack>
                    </Box>
                </Box>
            ) : (
            <>
            {/* 行事曆外框 */}
            <Box
                flex="1"
                display="flex"
                flexDirection="column"
                border="1.5px solid"
                borderColor="gray.200"
                borderRadius="xl"
                boxShadow="sm"
                bg="white"
                overflow="hidden"
            >
                {/* 星期標頭 */}
                <Grid templateColumns="repeat(7, 1fr)" gap={0} bg="gray.50" borderBottom="1.5px solid" borderColor="gray.200">
                    {WEEKDAYS.map((w, idx) => (
                        <GridItem key={w} borderLeft={idx === 0 ? 'none' : '1px solid'} borderColor="gray.100">
                            <Text
                                textAlign="center" fontSize="xs" fontWeight="bold" py={2}
                                color={idx === 0 || idx === 6 ? 'orange.400' : 'gray.500'}
                            >
                                {w}
                            </Text>
                        </GridItem>
                    ))}
                </Grid>

                {/* 日期格子 */}
                <Grid flex="1" templateColumns="repeat(7, 1fr)" templateRows={`repeat(${numRows}, 1fr)`} gap={0}>
                    {cells.map((cell, i) => {
                        if (!cell) {
                            const col = i % 7;
                            return (
                                <GridItem key={`empty-${i}`}
                                    borderRight={col < 6 ? '1px solid' : 'none'}
                                    borderBottom="1px solid"
                                    borderColor="gray.100"
                                    bg="gray.50"
                                />
                            );
                        }
                        const col = i % 7;
                        const { day, dateStr, isWeekend } = cell;
                        const count = monthlyCounts[dateStr] || 0;
                        const isSelected = selectedDates.has(dateStr);
                        const isToday = dateStr === today;
                        const hasOrders = count > 0;
                        // 最後一列不加 borderBottom（避免多餘線條）
                        const totalRows = Math.ceil(cells.length / 7);
                        const currentRow = Math.floor(i / 7);
                        const isLastRow = currentRow === totalRows - 1;

                        // 底色優先順序：有訂餐 > 今日 > 週末 > 一般
                        const cellBg = hasOrders
                            ? (isToday ? 'orange.100' : 'orange.50')
                            : isToday ? 'blue.50'
                            : isWeekend ? 'gray.50'
                            : 'white';
                        const hoverBg = hasOrders ? 'orange.100' : isToday ? 'blue.100' : 'gray.50';

                        return (
                            <GridItem key={dateStr}
                                borderRight={col < 6 ? '1px solid' : 'none'}
                                borderBottom={!isLastRow ? '1px solid' : 'none'}
                                borderColor="gray.100"
                                // 今日：頂部橘色條
                                borderTop={isToday ? '3px solid' : '0px'}
                                borderTopColor={isToday ? 'orange.400' : 'transparent'}
                            >
                                <Box
                                    position="relative"
                                    h="100%"
                                    bg={cellBg}
                                    cursor="pointer"
                                    _hover={{ bg: hoverBg }}
                                    onClick={() => handleCellClick(dateStr)}
                                    userSelect="none"
                                    transition="background 0.15s"
                                >
                                    {/* 日期數字 */}
                                    <Text
                                        pos="absolute" top="6px" left="8px"
                                        fontSize="sm" fontWeight={isToday ? 'bold' : 'normal'}
                                        color={isToday ? 'orange.600' : isWeekend ? 'gray.400' : 'gray.700'}
                                    >
                                        {day}
                                    </Text>

                                    {/* 跨日選取圓圈 */}
                                    <Box
                                        pos="absolute" top="6px" right="6px"
                                        w="16px" h="16px" borderRadius="full"
                                        border="2px solid"
                                        borderColor={isSelected ? 'orange.500' : 'gray.300'}
                                        bg={isSelected ? 'orange.500' : 'transparent'}
                                        cursor="pointer"
                                        onClick={(e) => toggleDateSelection(e, dateStr)}
                                        _hover={{ borderColor: 'orange.400' }}
                                        zIndex={1}
                                        transition="all 0.15s"
                                    />

                                    {/* 訂餐人數 */}
                                    {hasOrders && (
                                        <Badge
                                            pos="absolute" bottom="6px" left="8px"
                                            colorScheme="orange" fontSize="xs"
                                        >
                                            {count}人訂餐
                                        </Badge>
                                    )}
                                </Box>
                            </GridItem>
                        );
                    })}
                </Grid>
            </Box>

            {/* 使用說明 */}
            <Box mt={3} mb={2} px={1}>
                <HStack spacing={6} flexWrap="wrap" gap={2}>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="sm" bg="orange.50" border="1px solid" borderColor="orange.200" />
                        <Text fontSize="xs" color="var(--fg-muted)">有訂餐</Text>
                    </HStack>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="sm" bg="blue.50" borderTop="3px solid" borderColor="blue.50" borderTopColor="orange.400" />
                        <Text fontSize="xs" color="var(--fg-muted)">今日</Text>
                    </HStack>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="full" border="2px solid" borderColor="orange.500" bg="orange.500" />
                        <Text fontSize="xs" color="var(--fg-muted)">已勾選日期</Text>
                    </HStack>
                    <Text fontSize="xs" color="var(--fg-weak)">・點選日期格子查看／管理當日訂餐　・勾選圓圈可跨日選取，再選擇學生一次訂多天</Text>
                </HStack>
            </Box>
            </>
            )}

            {/* 跨日訂餐 Action Bar */}
            {selectedDates.size > 0 && (
                <Box
                    position="fixed"
                    bottom={{ base: 'calc(env(safe-area-inset-bottom) + 78px)', md: 6 }}
                    left="50%" transform="translateX(-50%)"
                    bg="orange.500" color="white" px={6} py={3} borderRadius="full"
                    boxShadow="xl" zIndex={100}
                >
                    <HStack spacing={4}>
                        <IconButton
                            icon={<FiX />} size="xs" variant="ghost" color="white"
                            onClick={() => setSelectedDates(new Set())}
                            aria-label="取消選取"
                        />
                        <Text fontWeight="bold" fontSize="sm">
                            已選 {selectedDates.size} 天
                        </Text>
                        <Button
                            size="sm" bg="white" color="orange.600"
                            _hover={{ bg: 'orange.50' }}
                            leftIcon={<FiUser />}
                            onClick={openBatch}
                        >
                            選擇學生 →
                        </Button>
                    </HStack>
                </Box>
            )}

            {/* 單日彈窗 */}
            <Modal isOpen={isDayOpen} onClose={closeDay} size="md" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack justify="space-between" pr={8}>
                            <Text fontSize="md">{dayTarget}</Text>
                            <Select
                                size="sm" w="160px" value={dayClassId}
                                onChange={e => {
                                    setDayClassId(e.target.value);
                                    if (dayTarget) fetchDayOrders(dayTarget, e.target.value);
                                }}
                            >
                                <option value="">全部班級</option>
                                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </Select>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {loadingDay ? (
                            <Box textAlign="center" py={6}><Spinner color="orange.400" /></Box>
                        ) : (
                            <VStack align="stretch" spacing={3}>
                                <Text fontWeight="bold" color="green.600" fontSize="sm">
                                    🍱 已訂餐（{dayOrders.length} 人）
                                </Text>
                                {dayOrders.length === 0 && (
                                    <Text fontSize="sm" color="var(--fg-weak)" pl={2}>尚無訂餐</Text>
                                )}
                                {dayOrders.map(o => (
                                    <HStack key={o.id} justify="space-between" pl={2}>
                                        <Text fontSize="sm">{o.student_name}</Text>
                                        <Button size="xs" colorScheme="red" variant="ghost"
                                            onClick={() => handleCancelOrder(o.id)}>取消</Button>
                                    </HStack>
                                ))}

                                <Divider />

                                <Text fontWeight="bold" color="var(--fg-body)" fontSize="sm">
                                    📋 未訂餐（{notOrdered.length} 人）
                                </Text>
                                {!dayClassId && (
                                    <Text fontSize="sm" color="var(--fg-weak)" pl={2} fontStyle="italic">
                                        請選擇特定班級以查看未訂餐名單
                                    </Text>
                                )}
                                {notOrdered.length === 0 && (
                                    <Text fontSize="sm" color="var(--fg-weak)" pl={2}>全班已訂餐</Text>
                                )}
                                {notOrdered.map(s => (
                                    <HStack key={s.id} pl={2}>
                                        <Checkbox
                                            isChecked={selectedToAdd.has(s.id)}
                                            onChange={() => {
                                                setSelectedToAdd(prev => {
                                                    const next = new Set(prev);
                                                    next.has(s.id) ? next.delete(s.id) : next.add(s.id);
                                                    return next;
                                                });
                                            }}
                                            colorScheme="orange"
                                        >
                                            <Text fontSize="sm">{s.full_name}</Text>
                                        </Checkbox>
                                    </HStack>
                                ))}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            colorScheme="orange"
                            isDisabled={selectedToAdd.size === 0}
                            isLoading={submittingDay}
                            onClick={handleAddOrders}
                            size="sm"
                        >
                            確認新增訂餐（{selectedToAdd.size} 人）
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <LunchSearchModal
                isOpen={isSearchOpen}
                onClose={closeSearch}
                onCancelSuccess={fetchMonthlySummary}
            />
            <LunchBatchModal
                isOpen={isBatchOpen}
                onClose={closeBatch}
                selectedDates={selectedDates}
                onSuccess={() => { setSelectedDates(new Set()); fetchMonthlySummary(); }}
            />
        </Box>
    );
};

export default LunchManagementPage;
